import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import { InviteLookup } from '../types';
import { ShieldAlert } from 'lucide-react';

export function Register() {
  const [token, setToken] = useState('');
  const [invite, setInvite] = useState<InviteLookup | null>(null);
  const [inviteLoading, setInviteLoading] = useState(true);
  const [inviteError, setInviteError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { register } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || '';
    setToken(t);
    if (!t) {
      setInviteLoading(false);
      return;
    }
    api.lookupInvite(t)
      .then((data) => {
        setInvite(data);
        if (data.email) setEmail(data.email);
        if (data.suggested_subdomain) setSubdomain(data.suggested_subdomain);
      })
      .catch((err) => setInviteError(err.message || 'Could not load invite'))
      .finally(() => setInviteLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invite || !token) return;
    setError('');
    setSubmitting(true);

    try {
      const result = await register({
        email,
        password,
        fullName,
        inviteToken: token,
        ...(invite.invite_type === 'new_org'
          ? { organizationName, subdomain: subdomain.toLowerCase() }
          : {}),
      });

      const userSubdomain = result?.organization?.subdomain;
      const protocol = window.location.protocol;
      window.location.href = `${protocol}//${userSubdomain}.taplo.cc/pos`;
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (inviteLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f4f8]">
        <div className="text-slate-500 font-medium">Loading invite…</div>
      </div>
    );
  }

  if (!token || !invite || inviteError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f4f8] font-sans p-6">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 max-w-[460px] w-full border border-slate-200/60 text-center">
          <div className="w-16 h-16 bg-amber-50 text-amber-500 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-amber-100">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">Registration is by invitation only</h1>
          <p className="text-[15px] font-medium text-slate-500 leading-relaxed">
            {inviteError
              ? `We couldn't load this invite: ${inviteError}`
              : 'Ask your administrator for an invite link to create an account.'}
          </p>
          <button
            type="button"
            onClick={() => { window.location.href = '/login'; }}
            className="mt-8 px-6 py-3 rounded-2xl bg-[#2563eb] text-white font-bold hover:bg-[#1d4ed8] transition-all active:scale-95"
          >
            Go to Sign In
          </button>
        </div>
      </div>
    );
  }

  if (invite.used || invite.expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f0f4f8] font-sans p-6">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 max-w-[460px] w-full border border-slate-200/60 text-center">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-red-100">
            <ShieldAlert className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">
            {invite.used ? 'This invite has been used' : 'This invite has expired'}
          </h1>
          <p className="text-[15px] font-medium text-slate-500 leading-relaxed">
            Contact your administrator to request a new invite link.
          </p>
        </div>
      </div>
    );
  }

  const isNewOrg = invite.invite_type === 'new_org';
  const lockEmail = !!invite.email;

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f0f4f8] font-sans p-6 selection:bg-blue-200">
      <div className="bg-white p-10 rounded-[2.5rem] shadow-xl shadow-slate-200/50 max-w-[460px] w-full border border-slate-200/60">
        <div className="flex flex-col items-center mb-8">
          <img src="/taplo-wordmark.png" alt="Taplo" className="h-12 md:h-14 mb-3 select-none" draggable={false} />
          <p className="mt-2 text-[15px] font-medium text-slate-500 text-center">
            {isNewOrg
              ? 'Create your workspace'
              : <>Joining <span className="text-[#1e40af] font-bold">{invite.organization_name}</span> as <span className="text-[#1e40af] font-bold">{invite.role}</span></>
            }
          </p>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-6 text-sm font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <div>
            <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Full Name</label>
            <input
              className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
              type="text"
              placeholder="Jane Doe"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              required
            />
          </div>

          {isNewOrg && (
            <>
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Organization Name</label>
                <input
                  className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  type="text"
                  placeholder="Saltwater POS"
                  value={organizationName}
                  onChange={e => setOrganizationName(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Subdomain</label>
                <div className="flex items-stretch h-14 relative group">
                  <input
                    type="text"
                    placeholder="your-org"
                    value={subdomain}
                    onChange={e => setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    required
                    className="flex-1 px-5 bg-white border border-slate-200 border-r-0 rounded-l-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
                  />
                  <span className="px-5 flex items-center bg-slate-50 border border-slate-200 border-l-0 rounded-r-2xl text-[15px] font-medium text-slate-400 whitespace-nowrap group-focus-within:border-[#2563eb] transition-colors">
                    .taplo.cc
                  </span>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Email</label>
            <input
              className={`w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300 ${lockEmail ? 'text-slate-500 bg-slate-50 cursor-not-allowed' : 'text-slate-800'}`}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={e => !lockEmail && setEmail(e.target.value)}
              readOnly={lockEmail}
              required
            />
            {lockEmail && (
              <p className="text-xs text-slate-400 mt-1.5 ml-1">This invite is locked to this email address.</p>
            )}
          </div>

          <div>
            <label className="block text-[13px] font-bold text-slate-500 uppercase tracking-wide mb-2 ml-1">Password</label>
            <input
              className="w-full h-14 px-5 bg-white border border-slate-200 rounded-2xl text-[16px] text-slate-800 font-medium focus:outline-none focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] transition-all placeholder-slate-300"
              type="password"
              placeholder="At least 6 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              minLength={6}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full h-14 mt-3 rounded-2xl flex items-center justify-center text-[16px] font-bold transition-all ${submitting ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-[#2563eb] text-white shadow-lg shadow-blue-900/20 hover:bg-[#1d4ed8] active:scale-[0.98]'}`}
          >
            {submitting ? 'Creating account…' : isNewOrg ? 'Create Workspace' : 'Join Workspace'}
          </button>
        </form>
      </div>
    </div>
  );
}
